/** AutoPay data-access layer — AutoPay summary derived from the shared `myMemberInfo` query, and enrollment. */

import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import { EnableMyAutoPayDocument, type EnableMyAutoPayFailureCode } from '@/graphql/generated/graphql';
import type { MyMemberInfo } from '@/hooks/member/memberInfoApi';

export interface MemberAutoPaySummary {
  autoPayEnrolled: boolean;
  /**
   * Recurring AutoPay amount, as shown in the UI. Sourced from `currentBalanceFormula`
   * (legacy `Current_Balance_Formula__c`) — matches legacy's own AutoPay modal, which displays
   * this same field under the label "Amount".
   */
  autoPayAmount: number | null;
  /** Payment plan / frequency (legacy `Current_Agreement_Payment_Schedule__c`). */
  autoPayPlan: string | null;
}

export function mapToAutoPaySummary(member: MyMemberInfo): MemberAutoPaySummary {
  return {
    autoPayEnrolled: member?.autoPay ?? false,
    autoPayAmount: member?.currentBalanceFormula ?? null,
    autoPayPlan: member?.currentAgreementPaymentSchedule ?? null,
  };
}

/**
 * Thrown when enableMyAutoPay returns its Failure variant. `ALREADY_ENABLED` is the only code —
 * callers treat it as a no-op, not a user-facing error (see useAddCreditCard/useAddBankAccount).
 */
export class EnableMyAutoPayError extends Error {
  constructor(public readonly code: EnableMyAutoPayFailureCode) {
    super(code);
    this.name = 'EnableMyAutoPayError';
  }
}

/**
 * Enrolls the caller in AutoPay. No-arg and member-scoped — not tied to any card/bank profile.
 * Idempotent: enableMyAutoPay treats an already-enrolled member as a no-op Failure, not an error.
 */
export async function requestEnableMyAutoPay(): Promise<void> {
  const data = await gqlRequest(EnableMyAutoPayDocument);
  const result = data.enableMyAutoPay;
  if (result.__typename === 'EnableMyAutoPayFailure') {
    throw new EnableMyAutoPayError(result.code);
  }
}
